import {
  type ApolloError,
  type DocumentNode,
  useApolloClient,
} from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { collectAllPages, type FetchPageFn } from './fetchAllPages';

type UseFetchAllPagesOptions<TData, TItem> = {
  query: DocumentNode;
  variables: Record<string, unknown>;
  extractPage: (data: TData) => { items: TItem[]; totalCount?: number };
  perPage?: number;
  skip?: boolean;
};

export const useFetchAllPages = <TData, TItem>({
  query,
  variables,
  extractPage,
  perPage = 50,
  skip = false,
}: UseFetchAllPagesOptions<TData, TItem>) => {
  const client = useApolloClient();
  const [data, setData] = useState<TItem[]>();
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<ApolloError>();
  const variablesKey = JSON.stringify(variables);
  const abortControllerRef = useRef<AbortController>();

  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const extractPageRef = useRef(extractPage);
  extractPageRef.current = extractPage;

  // biome-ignore lint/correctness/useExhaustiveDependencies: variablesKey is a serialized proxy for variables to trigger refetch on change
  const fetchAll = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(undefined);

    const currentVariables = variablesRef.current;
    const currentExtractPage = extractPageRef.current;

    const fetchPage: FetchPageFn<TItem> = async (offset, pageSize) => {
      const result = await client.query<TData>({
        query,
        variables: {
          ...currentVariables,
          input: {
            ...(currentVariables.input as Record<string, unknown>),
            pagination: {
              ...((currentVariables.input as Record<string, unknown>)
                ?.pagination as Record<string, unknown>),
              perPage: pageSize,
              offSet: offset,
            },
          },
        },
        fetchPolicy: 'no-cache',
        context: { fetchOptions: { signal: controller.signal } },
      });
      return currentExtractPage(result.data);
    };

    try {
      const allItems = await collectAllPages(
        fetchPage,
        perPage,
        controller.signal,
      );
      if (!controller.signal.aborted) {
        setData(allItems);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err as ApolloError);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [client, query, variablesKey, perPage]);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    fetchAll();
    return () => abortControllerRef.current?.abort();
  }, [skip, fetchAll]);

  return { data, loading, error, refetch: fetchAll };
};
